import React from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import {
  Sparkle,
  ArrowsClockwise,
  FileMagnifyingGlass,
  Stack,
} from "@phosphor-icons/react";
import {
  AnalysisEmptyDescription,
  AnalysisEmptyIconRing,
  AnalysisEmptyTitle,
  AnalysisEmptyWrapper,
} from "./AIPaneStyled";
import { brand, spacing, text, typography } from "../../theme/tokens";

/**
 * Readiness gate for collection-scoped chat, the group counterpart to
 * `DocumentChatGate`.
 *
 * The one real difference from the single-document gate is that this one
 * does NOT block on "everything ready". Twelve scans finish at twelve
 * different times, and the attorney should be able to start asking as
 * soon as the first is conversable — so `partial` renders nothing and
 * lets chat through, with a banner above the composer saying how many
 * members are actually being searched.
 *
 * Presentation-only: readiness and the prepare callback come from
 * `useCollectionAiReadiness` via ChatPane.
 */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningIcon = styled(ArrowsClockwise)`
  animation: ${spin} 1.6s linear infinite;
`;

const GateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[4]};
  border: none;
  border-radius: 8px;
  background: ${brand.primary};
  color: #ffffff;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GateErrorText = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: ${typography.size.sm};
  color: #b42318;
`;

const GateHintText = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: ${typography.size.sm};
  color: ${text.secondary};
`;

const CollectionChatGate = ({
  collectionName,
  readiness,
  loading,
  preparing,
  prepareError,
  onPrepare,
  onRetryStatus,
}) => {
  const state = readiness?.state || "not_ready";
  const prepareEnabled = readiness?.prepare_enabled !== false;
  const total = readiness?.total || 0;
  const ready = readiness?.ready || 0;
  const inProgress = readiness?.in_progress || 0;
  const failed = readiness?.failed || 0;

  if (loading) {
    return (
      <AnalysisEmptyWrapper data-testid="collection-chat-gate-loading">
        <AnalysisEmptyIconRing>
          <SpinningIcon size={32} />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>Checking AI readiness…</AnalysisEmptyTitle>
      </AnalysisEmptyWrapper>
    );
  }

  if (!readiness) {
    return (
      <AnalysisEmptyWrapper data-testid="collection-chat-gate-unavailable">
        <AnalysisEmptyIconRing>
          <FileMagnifyingGlass size={32} />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>Status unavailable</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          Couldn&apos;t check whether this collection is ready for AI chat.
        </AnalysisEmptyDescription>
        {onRetryStatus && (
          <GateButton onClick={onRetryStatus}>
            <ArrowsClockwise size={16} />
            Check again
          </GateButton>
        )}
      </AnalysisEmptyWrapper>
    );
  }

  if (state === "empty") {
    return (
      <AnalysisEmptyWrapper data-testid="collection-chat-gate-empty">
        <AnalysisEmptyIconRing>
          <Stack size={32} />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>This collection is empty</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          Select documents in the matter and use &ldquo;Save as
          collection&rdquo; to add them to {collectionName || "this group"}.
        </AnalysisEmptyDescription>
      </AnalysisEmptyWrapper>
    );
  }

  // Nothing is conversable yet but work is running. Once even one member
  // finishes the state flips to `partial` and this gate steps aside.
  if (state === "preparing") {
    return (
      <AnalysisEmptyWrapper data-testid="collection-chat-gate-progress">
        <AnalysisEmptyIconRing>
          <SpinningIcon size={32} />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>
          Preparing {inProgress} of {total} documents…
        </AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          Extracting and indexing {collectionName || "this collection"}. You
          can start asking questions as soon as the first document is ready —
          large files can take a while, and you can keep working meanwhile.
        </AnalysisEmptyDescription>
      </AnalysisEmptyWrapper>
    );
  }

  // not_ready: nothing prepared, nothing running.
  return (
    <AnalysisEmptyWrapper data-testid="collection-chat-gate-cta">
      <AnalysisEmptyIconRing>
        <Sparkle size={32} weight="fill" />
      </AnalysisEmptyIconRing>
      <AnalysisEmptyTitle>
        Chat with {collectionName || "this collection"}
      </AnalysisEmptyTitle>
      <AnalysisEmptyDescription>
        Prepare these {total} document{total === 1 ? "" : "s"} for AI
        questioning. You&apos;ll be able to ask things like &ldquo;is there a
        diagnosis for tinnitus anywhere in these records?&rdquo; and get back
        the document and page that shows it.
      </AnalysisEmptyDescription>
      <GateButton
        onClick={onPrepare}
        disabled={preparing || !prepareEnabled}
        data-testid="collection-chat-prepare-button"
      >
        <Sparkle size={16} weight="fill" />
        {preparing ? "Starting…" : `Prepare ${total} for AI chat`}
      </GateButton>
      {failed > 0 && (
        <GateHintText>
          {failed} document{failed === 1 ? "" : "s"} failed last time and will
          be retried.
        </GateHintText>
      )}
      {ready > 0 && (
        <GateHintText>
          {ready} already ready — they won&apos;t be prepared again.
        </GateHintText>
      )}
      {!prepareEnabled && (
        <GateHintText>
          AI document preparation is not enabled on this environment yet.
        </GateHintText>
      )}
      {prepareError && <GateErrorText>{prepareError}</GateErrorText>}
    </AnalysisEmptyWrapper>
  );
};

CollectionChatGate.propTypes = {
  collectionName: PropTypes.string,
  readiness: PropTypes.shape({
    state: PropTypes.string,
    total: PropTypes.number,
    ready: PropTypes.number,
    in_progress: PropTypes.number,
    failed: PropTypes.number,
    prepare_enabled: PropTypes.bool,
  }),
  loading: PropTypes.bool,
  preparing: PropTypes.bool,
  prepareError: PropTypes.string,
  onPrepare: PropTypes.func.isRequired,
  onRetryStatus: PropTypes.func,
};

CollectionChatGate.defaultProps = {
  collectionName: "",
  readiness: null,
  loading: false,
  preparing: false,
  prepareError: null,
  onRetryStatus: undefined,
};

/**
 * Shown above the composer while a collection is only partly prepared.
 * Silence here would be the dangerous option: an answer drawn from four
 * of twelve records looks identical to one drawn from all twelve, and
 * "not found" would read as "not in her records".
 */
const PartialBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin: 0 ${spacing[4]} ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid #fde68a;
  border-radius: 8px;
  background: #fffbeb;
  font-size: ${typography.size.sm};
  color: #92400e;
`;

export const CollectionPartialNotice = ({ readiness }) => {
  if (!readiness || readiness.state !== "partial") return null;
  const { ready = 0, total = 0, in_progress: inProgress = 0 } = readiness;
  return (
    <PartialBanner data-testid="collection-partial-notice">
      <SpinningIcon size={14} />
      <span>
        Searching {ready} of {total} documents
        {inProgress > 0 ? ` — ${inProgress} still preparing` : ""}. Answers
        won&apos;t cover the rest yet.
      </span>
    </PartialBanner>
  );
};

CollectionPartialNotice.propTypes = {
  readiness: PropTypes.shape({
    state: PropTypes.string,
    ready: PropTypes.number,
    total: PropTypes.number,
    in_progress: PropTypes.number,
  }),
};

CollectionPartialNotice.defaultProps = {
  readiness: null,
};

export default CollectionChatGate;
